import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type MyCallListsResult,
  type OpenCallListResult,
} from '@whinerp/client';
import { Button, EmptyState, ErrorState, PageHeading, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { defineScreen } from '../types.js';

/**
 * SC-74 Call queue (G-069).
 *
 * One person at a time, with their number, and the outcome logged in the same
 * step as moving on. The list beside it is context - how far through, who is
 * left - not the working surface: a rep on their fortieth call of the morning
 * should not have to find their place in a table.
 *
 * The outcome picker is six buttons rather than a select, because the whole
 * interaction is "press what happened, press next" and a dropdown puts two
 * extra actions between those.
 */

const OUTCOMES = ['connected', 'no-answer', 'voicemail', 'wrong-number', 'busy', 'callback-requested'] as const;

function CallQueueScreen(): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const queryClient = useQueryClient();
  const [listId, setListId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [notes, setNotes] = useState('');

  const lists = useQuery<MyCallListsResult, ApiError>({
    queryKey: ['call-lists'],
    queryFn: () => api.myCallLists({}),
  });

  const open = useQuery<OpenCallListResult, ApiError>({
    queryKey: ['call-list', listId],
    queryFn: () => api.openCallList({ path: { listId: listId ?? '' } }),
    enabled: listId !== null,
  });

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ['call-list', listId] });
    await queryClient.invalidateQueries({ queryKey: ['call-lists'] });
  };

  const work = useMutation({
    mutationFn: (input: { entryId: string; outcome: (typeof OUTCOMES)[number] }) =>
      api.workCallListEntry({
        path: { entryId: input.entryId },
        body: {
          outcome: input.outcome,
          // A summary somebody will read on the timeline six weeks later, not
          // "Call". The outcome is a field; this is the sentence.
          subject: subject.trim() || t(`record.call.outcome.${input.outcome}` as 'record.call.outcome.connected'),
          notes: notes.trim() || null,
          seconds: null,
        },
      }),
    onSuccess: async () => {
      setSubject('');
      setNotes('');
      await refresh();
    },
  });

  const skip = useMutation({
    mutationFn: (entryId: string) => api.skipCallListEntry({ path: { entryId }, body: {} }),
    onSuccess: refresh,
  });

  /*
   * The wrapper carries `data-component` in every state.
   *
   * A screen that identifies itself only once it has data is one nothing can
   * find while it is loading, failing or empty - which is most of the states
   * anybody debugging is looking at.
   */
  if (lists.isPending) {
    return (
      <div data-component="CallQueue" className="flex w-full flex-col gap-4">
        <Skeleton className="h-plot w-full" />
      </div>
    );
  }
  if (lists.isError) {
    return (
      <div data-component="CallQueue" className="flex w-full flex-col gap-4">
        <ErrorState title={t('state.error')} body={errorText(lists.error)} onRetry={() => void lists.refetch()} />
      </div>
    );
  }

  if (lists.data.lists.length === 0) {
    return (
      <div data-component="CallQueue" className="flex w-full flex-col gap-4">
        <EmptyState
          reason="no-records"
          illustration="Sparkle"
          title={t('callQueue.empty.title')}
          body={t('callQueue.empty.body')}
        />
      </div>
    );
  }

  const current = open.data?.next ?? null;

  return (
    <div data-component="CallQueue" className="flex w-full flex-col gap-4">
      <PageHeading>{t('callQueue.title')}</PageHeading>

      <div className="flex flex-col gap-4 lg:flex-row">
        <Panel className="lg:w-panel-narrow">
          <div className="flex flex-col gap-1">
            <SectionLabel>{t('callQueue.lists')}</SectionLabel>
            <ul className="flex flex-col">
              {lists.data.lists.map((list) => (
                <li key={list.listId}>
                  <button
                    type="button"
                    data-call-list={list.listId}
                    aria-current={listId === list.listId ? 'true' : undefined}
                    onClick={() => setListId(list.listId)}
                    className="w-full rounded-control px-2 py-1 text-start text-dense text-ink hover:bg-canvas aria-[current]:bg-bronze-tint"
                  >
                    {list.name}{' '}
                    {/* How far through, on the row: the first thing anybody
                        wants to know before opening one. */}
                    <span data-call-list-progress={list.listId} className="text-meta text-ink-meta">
                      {t('callQueue.progress', {
                        values: { done: String(list.done + list.skipped), total: String(list.total) },
                      })}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </Panel>

        <div className="flex min-w-0 flex-1 flex-col gap-4">
          {listId === null ? (
            <Panel>
              <p className="text-body text-ink-meta">{t('callQueue.pick')}</p>
            </Panel>
          ) : open.isPending ? (
            <Skeleton className="h-block-lg w-full" />
          ) : current === null ? (
            <Panel>
              <p data-call-queue-done className="text-body text-ink">
                {t('callQueue.finished')}
              </p>
            </Panel>
          ) : (
            <Panel data-region="current-call">
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-0.5">
                  <span className="text-h3 text-ink">{current.name}</span>
                  {/* A number somebody can press on a phone, and read on a
                      desk. */}
                  {current.phone ? (
                    <a data-call-number href={`tel:${current.phone}`} className="text-body text-bronze-700 underline">
                      {current.phone}
                    </a>
                  ) : (
                    <span data-call-no-number className="text-meta text-danger-text">{t('callQueue.noNumber')}</span>
                  )}
                </div>

                <input
                  data-call-subject
                  aria-label={t('callQueue.subject')}
                  placeholder={t('callQueue.subject')}
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  className="h-input rounded-control border border-control bg-surface px-3 text-body text-ink"
                />
                <textarea
                  data-call-notes
                  aria-label={t('callQueue.notes')}
                  placeholder={t('callQueue.notes')}
                  rows={3}
                  value={notes}
                  onChange={(event) => setNotes(event.target.value)}
                  className="rounded-control border border-control bg-surface px-3 py-2 text-body text-ink"
                />

                {/* Six buttons, not a dropdown: the interaction is "press what
                    happened", and a select puts two actions in front of it. */}
                <div role="group" aria-label={t('record.call.outcome')} className="flex flex-wrap gap-2">
                  {OUTCOMES.map((outcome) => (
                    <Button
                      key={outcome}
                      intent={outcome === 'connected' ? 'primary' : 'secondary'}
                      size="small"
                      data-call-outcome={outcome}
                      disabled={work.isPending}
                      onClick={() => work.mutate({ entryId: current.entryId, outcome })}
                    >
                      {t(`record.call.outcome.${outcome}` as 'record.call.outcome.connected')}
                    </Button>
                  ))}
                  <Button
                    intent="ghost"
                    size="small"
                    data-call-skip
                    disabled={skip.isPending}
                    onClick={() => skip.mutate(current.entryId)}
                  >
                    {t('callQueue.skip')}
                  </Button>
                </div>
                {work.error ? (
                  <p data-call-refusal className="text-dense text-danger-text">{errorText(work.error)}</p>
                ) : null}
              </div>
            </Panel>
          )}

          {open.data ? (
            <Panel data-region="queue">
              <ol className="flex flex-col">
                {open.data.list.entries.map((entry) => (
                  <li
                    key={entry.entryId}
                    data-queue-entry={entry.entryId}
                    data-queue-state={entry.state}
                    className="flex items-baseline gap-2 border-t border-t-rule py-1 text-dense first:border-t-0"
                  >
                    <span className={entry.state === 'waiting' ? 'text-ink' : 'text-ink-meta line-through'}>
                      {entry.name}
                    </span>
                    <span className="text-meta text-ink-meta">
                      {entry.state === 'skipped' ? t('callQueue.skipped') : entry.state === 'done' ? t('callQueue.called') : ''}
                    </span>
                  </li>
                ))}
              </ol>
            </Panel>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function QueueLoading(): JSX.Element {
  return <Skeleton className="h-plot w-full" />;
}
function QueueError(): JSX.Element {
  const t = useT();
  return <ErrorState title={t('state.error')} body={t('callQueue.empty.body')} />;
}
function QueueEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Sparkle"
      title={t('callQueue.empty.title')}
      body={t('callQueue.empty.body')}
    />
  );
}

export const screen = defineScreen({
  id: 'SC-74',
  route: '/call-queue',
  surface: 'page',
  title: 'Call queue',
  features: ['F-028'],
  entitlement: 'F-028',
  reachedBy: [{ kind: 'screen', from: 'SC-06', how: 'Call queue link above the contacts list' }],
  components: [],
  elements: ['CallQueue'],
  states: { empty: QueueEmpty, loading: QueueLoading, error: QueueError },
  component: CallQueueScreen,
});

export default screen;
